import { useState } from 'react'
import { format, isToday, isYesterday, isThisWeek } from 'date-fns'
import { History, Calendar, User } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { DatePicker } from '@/components/ui/date-picker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { HistoryTimelineItem } from '@/components/HistoryTimelineItem'
import { getDateRange, type DatePreset } from '@/components/ReportFilters'
import { useEntityHistory, useHistoryStats } from '@/hooks/use-api'
import type { HistoryRecord } from '@/types'

interface HistoryTimelineProps {
  entityId: string
  entityType?: string
  /** @deprecated date-range filter now always renders */
  showFilters?: boolean
}

export function HistoryTimeline({ entityId, entityType }: HistoryTimelineProps) {
  const [page, setPage] = useState(1)
  const limit = 25

  // Date-range filter for the change list (backend + hook already support
  // start_date/end_date). Empty = All Time (current behavior, nothing hidden).
  const [period, setPeriod] = useState<DatePreset>('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')

  const handlePeriodChange = (next: DatePreset) => {
    setPeriod(next)
    setPage(1)
    if (next === 'custom') {
      setStartDate(customFrom)
      setEndDate(customTo)
      return
    }
    const range = getDateRange(next)
    setStartDate(range.startDate || '')
    setEndDate(range.endDate || '')
  }

  const handleCustomDate = (which: 'from' | 'to', value: string) => {
    setPage(1)
    if (which === 'from') {
      setCustomFrom(value)
      setStartDate(value)
    } else {
      setCustomTo(value)
      setEndDate(value)
    }
  }

  // End-of-day so the chosen end date is inclusive (created_date is a timestamp).
  const startParam = startDate || undefined
  const endParam = endDate ? `${endDate}T23:59:59.999` : undefined

  const { data, isLoading, error } = useEntityHistory({
    entity_id: entityId,
    page,
    limit,
    start_date: startParam,
    end_date: endParam,
  })

  // Summary cards use the same range so they stay consistent with the list.
  const { data: stats } = useHistoryStats(entityId, {
    start_date: startParam,
    end_date: endParam,
  })

  const records = data?.data ?? []
  const total = data?.total ?? 0
  const currentPage = data?.page ?? 1
  const totalPages = Math.ceil(total / limit)

  // Group records by date
  const groupRecordsByDate = (records: HistoryRecord[]) => {
    const groups: Record<string, HistoryRecord[]> = {}

    records.forEach((record) => {
      const date = new Date(record.change_date)
      let groupKey: string

      if (isToday(date)) {
        groupKey = 'Today'
      } else if (isYesterday(date)) {
        groupKey = 'Yesterday'
      } else if (isThisWeek(date)) {
        groupKey = 'This Week'
      } else {
        groupKey = format(date, 'MMMM yyyy')
      }

      if (!groups[groupKey]) {
        groups[groupKey] = []
      }
      groups[groupKey]!.push(record)
    })

    return groups
  }

  const groupedRecords = groupRecordsByDate(records)

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" aria-hidden="true" />
            History Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8" role="status" aria-live="polite">
            <p className="text-muted-foreground">Loading history...</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" aria-hidden="true" />
            History Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-destructive" role="alert">
            Error loading history. Please try again.
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Changes</CardTitle>
              <History className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_changes.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">Across {stats.unique_fields} fields</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Modified By</CardTitle>
              <User className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.unique_users}</div>
              <p className="text-xs text-muted-foreground">
                {stats.unique_users === 1 ? 'user' : 'different users'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Date Range</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent>
              {stats.date_range ? (
                <>
                  <div className="text-sm font-semibold">
                    {format(new Date(stats.date_range.earliest), 'MMM d, yyyy')}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    to {format(new Date(stats.date_range.latest), 'MMM d, yyyy')}
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No changes recorded</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Changed Fields */}
      {stats?.top_changed_fields && stats.top_changed_fields.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Most Changed Fields</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {stats.top_changed_fields.slice(0, 10).map((field) => (
                <Badge key={field.field_name} variant="secondary" className="gap-1">
                  {field.field_label}
                  <span className="text-xs text-muted-foreground">({field.change_count})</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* History Timeline */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" aria-hidden="true" />
              Change History
              {total > 0 && (
                <span className="text-sm font-normal text-muted-foreground">
                  ({total.toLocaleString()} total)
                </span>
              )}
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={period} onValueChange={(v) => handlePeriodChange(v as DatePreset)}>
                <SelectTrigger
                  className="w-[180px] h-9"
                  aria-label="Filter change history by date range"
                >
                  <Calendar
                    className="h-3.5 w-3.5 mr-1.5 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="last_90_days">Last 90 Days</SelectItem>
                  <SelectItem value="last_12_months">Last 12 Months</SelectItem>
                  <SelectItem value="calendar_year">This Calendar Year</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
              {period === 'custom' && (
                <>
                  <DatePicker
                    value={customFrom}
                    onChange={(v) => handleCustomDate('from', v ?? '')}
                    className="h-9 w-[150px]"
                    aria-label="History start date"
                  />
                  <DatePicker
                    value={customTo}
                    onChange={(v) => handleCustomDate('to', v ?? '')}
                    className="h-9 w-[150px]"
                    aria-label="History end date"
                  />
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {records.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No change history found for this {entityType || 'record'}.
            </div>
          ) : (
            <div className="space-y-6" role="feed" aria-label="History timeline">
              {Object.entries(groupedRecords).map(([dateGroup, groupRecords]) => (
                <div key={dateGroup}>
                  {/* Date Group Header */}
                  <div className="sticky top-0 bg-background py-2 mb-4">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-700">{dateGroup}</h3>
                      <Separator className="flex-1" />
                      <Badge variant="outline" className="text-xs">
                        {groupRecords.length} {groupRecords.length === 1 ? 'change' : 'changes'}
                      </Badge>
                    </div>
                  </div>

                  {/* Records in this date group */}
                  <div className="space-y-3">
                    {groupRecords.map((record) => (
                      <HistoryTimelineItem key={record.id} record={record} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <>
              <Separator className="my-6" />
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
